import { v4 as uuidv4 } from 'uuid';
import { YEAR_IN_SECONDS } from 'core/common/constants';
import { Config } from 'core/common/contexts';
import { normalizeError } from 'core/common/errors';
import { CookiesService } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { FeatureRawResult, FeatureValue } from '../entities';
import { FeatureFlags } from './FeatureFlags';
import { FeatureFlagsDecorator } from './FeatureFlagsDecorator';

export class CookiesIdFeatureFlagsDecorator extends FeatureFlagsDecorator {
  private cookies: CookiesService;

  private logger: Logger;

  private config: Config;

  readonly storageKey = 'split_id';

  private currentIdentifier = '';

  constructor(featureFlags: FeatureFlags, cookies: CookiesService, config: Config, logger: Logger) {
    super(featureFlags);
    this.cookies = cookies;
    this.logger = logger;
    this.config = config;
  }

  getSplitId(): string {
    this.maybeUpdateFeatureFlagsId();
    return super.getSplitId();
  }

  getExperimentGroup<T extends FeatureValue>(experimentName: string): T | null {
    this.maybeUpdateFeatureFlagsId();
    return super.getExperimentGroup(experimentName);
  }

  getFeatureValue<T extends FeatureValue = FeatureValue>(featureName: string, defaultValue?: T): T {
    this.maybeUpdateFeatureFlagsId();
    return super.getFeatureValue(featureName, defaultValue);
  }

  getFeatureRawResult<T extends FeatureValue>(featureName: string): FeatureRawResult<T | null> {
    this.maybeUpdateFeatureFlagsId();
    return super.getFeatureRawResult(featureName);
  }

  isFeatureOn(featureName: string): boolean {
    this.maybeUpdateFeatureFlagsId();
    return super.isFeatureOn(featureName);
  }

  isFeatureOff(featureName: string): boolean {
    this.maybeUpdateFeatureFlagsId();
    return super.isFeatureOff(featureName);
  }

  private maybeUpdateFeatureFlagsId() {
    const newIdentifier = this.getIdentifier();

    if (newIdentifier === this.currentIdentifier) return;

    this.currentIdentifier = newIdentifier;
    super.setIdentifier(newIdentifier);
  }

  private getIdentifier() {
    if (this.currentIdentifier) return this.currentIdentifier;

    const cookiesId = this.getIdentifierFromCookies();

    if (cookiesId) {
      return cookiesId;
    }

    return this.createIdentifier();
  }

  private createIdentifier() {
    const identifier = uuidv4();

    try {
      this.cookies.set(this.storageKey, identifier, {
        maxAge: YEAR_IN_SECONDS,
        path: '/',
      });

      return identifier;
    } catch (e) {
      const err = normalizeError(e);

      this.logger.error('Failed to save identifier', {
        data: {
          errorMessage: err.message,
        },
      });

      return identifier;
    }
  }

  private getIdentifierFromCookies(): string {
    try {
      return this.cookies.get(this.storageKey) || '';
    } catch (e) {
      const err = normalizeError(e);

      this.logger.error('Failed to load identifier from cookies', {
        data: {
          errorMessage: err.message,
        },
      });
      return '';
    }
  }
}
